import { useEffect, useState } from 'react';
import { siteInfo } from '@/mocks/data';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formError, setFormError] = useState('');
  const [formSuccess, setFormSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, []);

  const aboutJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'AboutPage',
    name: `关于${siteInfo.name}`,
    description: siteInfo.brandIntro,
    url: `${siteInfo.url}/about`,
    mainEntity: {
      '@type': 'Organization',
      name: siteInfo.name,
      description: siteInfo.description,
      contactPoint: {
        '@type': 'ContactPoint',
        email: siteInfo.contact.email,
        telephone: siteInfo.contact.phone,
        contactType: 'customer service',
        availableLanguage: ['Chinese'],
      },
      address: {
        '@type': 'PostalAddress',
        addressLocality: '北京',
        addressCountry: 'CN',
        streetAddress: siteInfo.contact.address,
      },
    },
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError('');
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('phone_alt') as string || '').trim();
    if (honeypot) {
      setFormSuccess(true);
      form.reset();
      return;
    }

    formData.delete('phone_alt');

    try {
      const response = await fetch('https://readdy.ai/api/form/d9u0vv53pcjqs2fcska0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormSuccess(true);
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        if (serverMsg.includes('spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(serverMsg || '提交失败，请稍后重试');
        }
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(aboutJsonLd)}</script>
      <div className="pt-20 pb-16">
        <Breadcrumb
          items={[
            { label: '首页', href: '/' },
            { label: '关于我们' },
          ]}
        />

        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
          <div className="max-w-[800px] mx-auto">
            <section className="mb-16">
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-6">
                关于我们
              </h1>
              <p className="text-foreground-700 leading-relaxed text-base">
                {siteInfo.brandIntro}
              </p>
            </section>

            <section className="mb-16">
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-8">
                我们的口碑
              </h2>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                {siteInfo.reputation.map((item, index) => (
                  <div
                    key={index}
                    className="bg-background-50 border border-background-200/70 rounded-lg p-6 text-center"
                  >
                    <div className="text-2xl md:text-3xl font-heading font-bold text-accent-500 mb-2">
                      {item.metric}
                    </div>
                    <div className="text-sm text-foreground-600">
                      {item.label}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section>
              <h2 className="text-xl font-heading font-bold text-foreground-950 mb-8">
                联系我们
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
                <div className="space-y-5">
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-mail-line"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">邮箱</h3>
                      <p className="text-sm text-foreground-600">{siteInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-phone-line"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">电话</h3>
                      <p className="text-sm text-foreground-600">{siteInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-wechat-line"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">微信</h3>
                      <p className="text-sm text-foreground-600">{siteInfo.contact.wechat}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-accent-100 text-accent-600">
                      <i className="ri-map-pin-line"></i>
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground-900 mb-1">地址</h3>
                      <p className="text-sm text-foreground-600">{siteInfo.contact.address}</p>
                    </div>
                  </div>
                </div>

                <div>
                  {formSuccess ? (
                    <div className="bg-accent-50 border border-accent-200 rounded-lg p-6 text-center">
                      <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                        <i className="ri-check-line text-xl"></i>
                      </div>
                      <h3 className="text-base font-semibold text-foreground-950 mb-1">发送成功</h3>
                      <p className="text-sm text-foreground-600">感谢您的留言，我们会尽快回复。</p>
                    </div>
                  ) : (
                    <form
                      onSubmit={handleSubmit}
                      data-readdy-form=""
                      className="bg-background-50 border border-background-200/70 rounded-lg p-6 space-y-4"
                    >
                      <h3 className="text-base font-heading font-semibold text-foreground-950 mb-2">
                        发送消息
                      </h3>

                      {formError && (
                        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
                          {formError}
                        </div>
                      )}

                      <div>
                        <label htmlFor="about-name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                        <input
                          id="about-name"
                          name="name"
                          type="text"
                          required
                          className="w-full px-4 py-2.5 border border-background-200 rounded-md text-sm text-foreground-900 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的姓名"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                        <input
                          id="about-email"
                          name="email"
                          type="email"
                          required
                          className="w-full px-4 py-2.5 border border-background-200 rounded-md text-sm text-foreground-900 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                          placeholder="请输入您的邮箱"
                        />
                      </div>
                      <div>
                        <label htmlFor="about-message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                        <textarea
                          id="about-message"
                          name="message"
                          rows={4}
                          required
                          maxLength={500}
                          className="w-full px-4 py-2.5 border border-background-200 rounded-md text-sm text-foreground-900 bg-background-50 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                          placeholder="请输入您的留言（最多500字）"
                        ></textarea>
                      </div>

                      <div className="honeypot-wrap">
                        <label htmlFor="phone-alt">Phone</label>
                        <input
                          id="phone-alt"
                          name="phone_alt"
                          type="text"
                          tabIndex={-1}
                          autoComplete="off"
                          aria-hidden="true"
                          readOnly
                        />
                      </div>

                      <button
                        type="submit"
                        className="w-full px-6 py-3 bg-primary-500 text-background-50 rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
                      >
                        发送留言
                      </button>
                    </form>
                  )}
                </div>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}